import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { CHART_BORDER_WIDTH, CHART_FILL_ALPHA, getSeriesColors } from '../../dashboardChartColors';

const DEFAULT_HEIGHT = 280;

function DashboardBarChart({
    series = [],
    horizontal = false,
    percentage = false,
    solidColors = false,
    showLegend = false,
    height,
    seriesName = 'Count',
}) {
    const categories = series.map((item) => item.label);
    const values = series.map((item) => item.value);
    const colors = getSeriesColors(series);
    const chartHeight =
        height ||
        (horizontal ? Math.max(DEFAULT_HEIGHT, series.length * 36 + 48) : DEFAULT_HEIGHT);

    const options = {
        chart: {
            type: 'bar',
            toolbar: { show: false },
            animations: { enabled: true, easing: 'easeinout', speed: 800 },
        },
        colors,
        fill: { type: 'solid', opacity: solidColors ? 1 : CHART_FILL_ALPHA },
        stroke: {
            show: true,
            width: CHART_BORDER_WIDTH,
            colors,
        },
        states: {
            hover: { filter: { type: 'darken', value: 0.12 } },
        },
        plotOptions: {
            bar: {
                horizontal,
                borderRadius: 8,
                borderRadiusApplication: 'end',
                distributed: true,
                columnWidth: horizontal ? '55%' : '48%',
                barHeight: horizontal ? '65%' : undefined,
            },
        },
        dataLabels: {
            enabled: solidColors && !horizontal,
            style: { fontSize: '11px', fontWeight: 600, colors: ['#ffffff'] },
            formatter: (val) => (Number(val) ? Number(val).toLocaleString() : ''),
        },
        legend: {
            show: showLegend,
            position: 'bottom',
            horizontalAlign: 'center',
            fontSize: '12px',
            markers: { width: 10, height: 10, radius: 2 },
            itemMargin: { horizontal: 8, vertical: 4 },
            onItemClick: { toggleDataSeries: false },
            onItemHover: { highlightDataSeries: false },
        },
        xaxis: {
            categories,
            labels: {
                rotate: horizontal ? 0 : -25,
                style: { fontSize: horizontal ? '12px' : '11px', colors: '#6c757d' },
                maxHeight: horizontal ? undefined : 80,
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            min: 0,
            max: percentage ? 100 : undefined,
            forceNiceScale: !percentage,
            labels: {
                style: { colors: '#6c757d', fontSize: '12px' },
                formatter: percentage ? (val) => `${val}%` : undefined,
                maxWidth: horizontal ? 160 : undefined,
            },
        },
        grid: {
            borderColor: '#f1f3f8',
            strokeDashArray: 4,
            padding: { left: 8, right: 8 },
        },
        tooltip: {
            custom: ({ dataPointIndex }) => {
                const item = series[dataPointIndex];
                if (!item) {
                    return '';
                }

                const valueText = percentage
                    ? `${Number(item.value).toFixed(1)}%`
                    : Number(item.value).toLocaleString();
                const extra = item.tooltipExtra
                    ? `<div class="dashboard-bar-tooltip__extra">${item.tooltipExtra}</div>`
                    : '';

                return `
                    <div class="dashboard-bar-tooltip">
                        <div class="dashboard-bar-tooltip__swatch" style="background:${item.color || colors[dataPointIndex]}"></div>
                        <div class="dashboard-bar-tooltip__body">
                            <div class="dashboard-bar-tooltip__label">${item.label}</div>
                            <div class="dashboard-bar-tooltip__value">${valueText}</div>
                            ${extra}
                        </div>
                    </div>
                `;
            },
        },
    };

    return (
        <ReactApexChart
            options={options}
            series={[{ name: seriesName, data: values }]}
            type="bar"
            height={chartHeight}
            width="100%"
        />
    );
}

export default DashboardBarChart;
